import { API_BASE_URL } from "../constants/appConstants";

const Auth_BASE_URL = `${API_BASE_URL}/accounts`;

// Request password reset (send email)
export const requestPasswordReset = async (email) => {
  const response = await fetch(`${Auth_BASE_URL}/password-reset/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to send password reset email');
  }
  return data;
};


export const confirmPasswordReset = async (uidb64, token, new_password1, new_password2) => {
  const response = await fetch(`${Auth_BASE_URL}/password-reset-confirm/${uidb64}/${token}/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_password1, new_password2 })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to reset password');
  }
  return data;
};

export const loginUser = async (login, password, userType = 'user') => {
  const response = await fetch(`${Auth_BASE_URL}/login/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ login, password, user_type: userType }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    // Extract the actual error message from backend
    const errorMessage = errorData.error || errorData.detail || errorData.message || 'Login failed';
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data; // Returns { access, refresh, user }
};

export const registerUser = async (userData) => {
  const response = await fetch(`${Auth_BASE_URL}/register/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    // Throw a more specific error, potentially with backend details
    throw new Error(JSON.stringify(errorData) || 'Registration failed'); 
  }

  // Registration successful, return nothing or a success indicator
  // The context will handle the subsequent login
  return true; 
};

export const refreshTokenRequest = async (refreshToken) => {
  if (!refreshToken) {
    throw new Error('No refresh token provided');
  }

  const response = await fetch(`${Auth_BASE_URL}/refresh/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  return data; // Returns { access }
};

export const logoutUser = async (refreshToken) => {
  if (!refreshToken) {
    // Although authenticatedRequest handles access token, we explicitly need refresh here
    throw new Error('No refresh token provided for logout'); 
  }

  // We use authenticatedRequest to ensure the access token is valid or refreshed first,
  // as the backend endpoint requires authentication.
  const response = await authenticatedRequest(`${Auth_BASE_URL}/logout/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  // Check if the response status indicates success (e.g., 200 OK)
  // The backend might return 200 or 204 depending on implementation
  if (!response.ok && response.status !== 204) { 
    const errorData = await response.text(); // Or response.json() if applicable
    throw new Error(`Logout failed: ${errorData}`);
  }

  // Logout successful on the backend
  // console.log("Backend logout successful (token blacklisted).");
  return true; 
};

// Optional: A generic authenticated fetch service function
// This might be better placed in a separate apiService.js if you have many API calls

export const authenticatedRequest = async (url, options = {}) => {
  let accessToken = localStorage.getItem('accessToken');
  
  if (!options.headers) {
    options.headers = {};
  }
  
  // Ensure Authorization header is set correctly
  options.headers.Authorization = `Bearer ${accessToken}`;

  try {
    let response = await fetch(url, options);

    if (response.status === 401) {
      console.log('Access token expired or invalid, attempting refresh...');
      const refresh = localStorage.getItem('refreshToken');
      try {
        const refreshData = await refreshTokenRequest(refresh);
        accessToken = refreshData.access;
        localStorage.setItem('accessToken', accessToken);
        console.log('Token refreshed successfully.');
        
        // Retry the original request with the new token
        options.headers.Authorization = `Bearer ${accessToken}`;
        response = await fetch(url, options); 
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        // Handle failed refresh (e.g., logout user) - This might be better handled in the context
        throw new Error('Session expired. Please login again.'); 
      }
    }
    
    const data = response.status === 204 ? [] : await response.json();

    if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.error || data.detail || 'Failed to fetch dashboard statistics',
          errorData: data,
        };
    }

    return {
        success: true,
        status: response.status,
        data,
    };
  } catch (error) {
    console.error('Authenticated request error:', error);
    throw error; // Re-throw the error to be caught by the caller
  }
};



// Base authenticated fetch service function - returns raw response
// Use this for file downloads (PDF, Excel) or when you need full response control
export const authenticatedFilesRequest = async (url, options = {}) => {
  let accessToken = localStorage.getItem('accessToken');
  
  if (!options.headers) {
    options.headers = {};
  }
  
  // Ensure Authorization header is set correctly
  options.headers.Authorization = `Bearer ${accessToken}`;

  try {
    let response = await fetch(url, options);

    if (response.status === 401) {
      console.log('Access token expired or invalid, attempting refresh...');
      const refresh = localStorage.getItem('refreshToken');
      try {
        const refreshData = await refreshTokenRequest(refresh);
        accessToken = refreshData.access;
        localStorage.setItem('accessToken', accessToken);
        console.log('Token refreshed successfully.');
        
        // Retry the original request with the new token
        options.headers.Authorization = `Bearer ${accessToken}`;
        response = await fetch(url, options); 
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        throw new Error('Session expired. Please login again.'); 
      }
    }
    
    // Return the raw response for the caller to handle
    // Download functions can call response.blob()
    // Other functions can call response.json() or response.text()
    return response;
    
  } catch (error) {
    console.error('Authenticated request error:', error);
    throw error;
  }
};

